import { useMemo, useState } from 'react';
import { SIN_CLIENTE, type OpcionCliente } from '@/lib/cuadro/filtros';

// Filtro por cliente (change-002). Desplegable con buscador y no chips como los
// otros tres: un periodo de 30 días con tráfico real trae decenas de clientes
// distintos y la fila de chips sería inmanejable.
//
// `<select>` nativo con un campo de filtro delante, no un combobox propio: sin
// librería de UI en el proyecto, lo nativo ya trae teclado, lector de pantalla y
// el desplegable del sistema en móvil, que es más de lo que daría un div con
// aria puesto a mano. Y `<datalist>` queda descartado porque devuelve el TEXTO
// escrito, no el uuid, y aquí se filtra por id (P1).

// Extraída y exportada para poder probarla: la regla de la opción activa es
// justo la que no se ve al mirar la pantalla y la que rompe el filtro si falta.
export function _visibles(
  clientes: OpcionCliente[],
  busqueda: string,
  valor: string | null
): OpcionCliente[] {
  const q = busqueda.trim().toLowerCase();
  if (!q) return clientes;
  const coincide = clientes.filter((c) => c.razonSocial.toLowerCase().includes(q));
  // La opción activa nunca se filtra fuera: si el `value` del select no está
  // entre sus `option`, el navegador lo pinta vacío y parece que se ha perdido
  // el filtro cuando en realidad sigue aplicado.
  const activa = clientes.find((c) => c.id === valor);
  if (activa && !coincide.some((c) => c.id === activa.id)) return [activa, ...coincide];
  return coincide;
}

export function SelectorCliente({
  valor,
  clientes,
  sinCliente,
  onCambiar,
}: {
  valor: string | null;
  clientes: OpcionCliente[];
  sinCliente: number;
  onCambiar: (cliente: string | null) => void;
}) {
  const [busqueda, setBusqueda] = useState('');

  const visibles = useMemo(() => _visibles(clientes, busqueda, valor), [clientes, busqueda, valor]);

  if (clientes.length === 0 && sinCliente === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      <span className="text-[11.5px] font-medium">Cliente</span>

      {/* El buscador solo aparece cuando hay bastantes para que ayude. */}
      {clientes.length > 8 && (
        <input
          type="search"
          aria-label="Buscar cliente"
          placeholder="Buscar…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="w-28 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-[12px] text-[var(--navy)] focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)]"
        />
      )}

      <select
        aria-label="Filtrar por cliente"
        value={valor ?? ''}
        onChange={(e) => onCambiar(e.target.value === '' ? null : e.target.value)}
        className="min-w-0 max-w-full sm:max-w-[16rem] rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12.5px] font-medium text-[var(--navy)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)] cursor-pointer"
      >
        <option value="">Todos los clientes</option>
        {sinCliente > 0 && (
          <option value={SIN_CLIENTE}>Sin cliente identificado ({sinCliente})</option>
        )}
        {visibles.map((c) => (
          <option key={c.id} value={c.id}>
            {c.razonSocial} ({c.total})
          </option>
        ))}
      </select>
    </div>
  );
}
